<template>
  <v-container fluid class="width-100" v-if="getPermission('setting:view')">
    <v-layout>
      <v-flex>
        <h3
          class="font-weight-700 mb-0 font-size-24 custom-headline color-custom-blue"
        >
          Currency
        </h3>
      </v-flex>
      <v-flex class="text-right">
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button"
          v-on:click="goBack"
        >
          <v-icon small left>mdi-keyboard-backspace</v-icon>
          Back
        </v-btn>
      </v-flex>
    </v-layout>

    <v-row>
      <v-col md="10">
        <perfect-scrollbar
          :options="{ suppressScrollX: true }"
          style="height: calc(100vh - 140px); position: relative"
        >
          <v-card
            flat
            class="custom-grey-border remove-border-radius mt-0 currency-list-card"
          >
            <v-card-title class="headline grey lighten-4">
              <v-row>
                <v-col md="4" class="py-0 my-auto">
                  <h3
                    class="font-weight-700 custom-headline color-custom-blue"
                    style="margin: 3.5px 0px"
                  >
                    Add/Edit Currency
                  </h3>
                </v-col>
                <template v-if="getPermission('setting:update')">
                  <v-col md="8" class="text-right py-0">
                    <v-btn
                      depressed
                      small
                      class="white--text"
                      color="cyan"
                      @click="openCurrencyDialog"
                    >
                      <span class="font-size-16 font-weight-600">Create</span>
                    </v-btn>
                  </v-col>
                </template>
              </v-row>
            </v-card-title>

            <v-card-text class="p-6 pb-16 font-size-16">
              <v-row>
                <v-col md="12" class="pb-4">
                  <v-simple-table
                    fixed-header
                    class="simple-table"
                    style="min-height: 200px"
                  >
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="simple-table-th" width="50">#</th>
                          <th class="simple-table-th">Currency Name</th>
                          <th class="simple-table-th" width="120">Status</th>
                          <th class="simple-table-th" width="100">Action</th>
                        </tr>
                      </thead>
                      <tbody v-if="paginatedCurrencyList.length > 0">
                        <tr
                          v-for="(currency, index) in paginatedCurrencyList"
                          :key="index"
                          class="alternate-listing-row"
                        >
                          <td>
                            {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                          </td>
                          <td class="text-capitalize">{{ currency.name }}</td>

                          <!-- ✅ Direct Toggle Switch -->
                          <td>
                            <v-switch
                              v-model="currency.activated"
                              color="green"
                              inset
                              hide-details
                              @change="toggleCurrencyStatus(currency)"
                            ></v-switch>
                          </td>

                          <td>
                            <v-icon
                              color="cyan"
                              size="20"
                              @click="editCurrency(currency)"
                              >mdi-pencil</v-icon
                            >
                            <v-icon
                              color="red"
                              size="20"
                              class="ml-2"
                              @click="openDeleteDialog(currency)"
                            >
                              mdi-delete
                            </v-icon>
                          </td>
                        </tr>
                      </tbody>
                      <tr v-else>
                        <td colspan="4" class="py-4 text-center text-muted">
                          No currencies found.
                        </td>
                      </tr>
                    </template>
                  </v-simple-table>
                </v-col>
              </v-row>

              <!-- Pagination -->
              <v-row
                v-if="currencyList.length"
                justify="end"
                class="align-center pagination_btn listing-footer currency-list-footer px-2"
              >
                <p class="m-0 listing-footer-count">
                  Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                  {{
                    Math.min(currentPage * itemsPerPage, currencyList.length)
                  }}
                  of {{ currencyList.length }} entries
                </p>
                <v-spacer></v-spacer>
                <v-pagination
                  v-model="currentPage"
                  :length="pageCount"
                  total-visible="7"
                  color="cyan"
                  @input="handlePageChange"
                ></v-pagination>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Add/Edit Dialog -->
          <Dialog :commonDialog="currencyDialog" :dialog-width="600">
            <template v-slot:title>
              <template v-if="currencyPayload.id">Edit</template>
              <template v-else>Add</template> Currency
            </template>
            <template v-slot:body>
              <v-container fluid>
                <v-form
                  ref="currencyForm"
                  v-model.trim="formValid"
                  lazy-validation
                >
                  <v-row>
                    <v-col md="12">
                      <label class="fw-500 required mb-1">Currency Name</label>
                      <TextInput
                        v-model.trim="currencyPayload.name"
                        placeholder="Enter currency name"
                        :rules="[
                          validateRules.required(
                            currencyPayload.name,
                            'Currency Name'
                          ),
                        ]"
                        color="cyan"
                      />
                    </v-col>
                  </v-row>
                </v-form>
              </v-container>
            </template>
            <template v-slot:action>
              <v-btn
                depressed
                :loading="pageLoading"
                :disabled="pageLoading || !formValid"
                color="cyan white--text"
                @click="saveCurrency"
              >
                Save
              </v-btn>
              <v-btn
                depressed
                :disabled="pageLoading"
                @click="currencyDialog = false"
              >
                Close
              </v-btn>
            </template>
          </Dialog>

          <!-- Delete Dialog -->
          <DeleteDialog :delete-dialog="deleteDialog">
            <template v-slot:title>Delete Confirmation</template>
            <template v-slot:text>
              <h3 class="font-size-18 font-weight-600 delete-text">
                Are you sure you want to delete this currency?
              </h3>
            </template>
            <template v-slot:action>
              <v-btn
                depressed
                :disabled="deleteLoading"
                @click="deleteDialog = false"
                >No! Cancel</v-btn
              >
              <v-btn
                depressed
                color="red lighten-1 white--text"
                :loading="deleteLoading"
                @click="confirmDelete"
              >
                Yes! Delete
              </v-btn>
            </template>
          </DeleteDialog>
        </perfect-scrollbar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { GET, PATCH, DELETE } from "@/core/services/store/request.module";
import Dialog from "@/view/pages/partials/Dialog.vue";
import TextInput from "@/view/components/TextInput.vue";
import DeleteDialog from "@/view/pages/partials/Delete";
import ValidationMixin from "@/core/plugins/validation-mixin";
import {
  ErrorEventBus,
  InitializeError,
  SuccessEventBus,
} from "@/core/lib/message.lib";

export default {
  name: "currency-setting",
  mixins: [ValidationMixin],
  components: { Dialog, TextInput, DeleteDialog },
  data() {
    return {
      formValid: false,
      pageLoading: false,
      deleteLoading: false,
      itemsPerPage: 10,
      currentPage: 1,
      currencyList: [],
      currencyDialog: false,
      deleteDialog: false,
      currencyPayload: {
        id: null,
        name: "",
        activated: true,
      },
      selectedCurrencyId: null,
    };
  },
  computed: {
    pageCount() {
      return Math.ceil(this.currencyList.length / this.itemsPerPage);
    },
    paginatedCurrencyList() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.currencyList.slice(start, end);
    },
  },
  methods: {
    // Fetch all currencies
    getCurrencies() {
      this.pageLoading = true;
      this.$store
        .dispatch(GET, { url: "setting/currency" })
        .then((res) => {
          this.currencyList = res.data || [];
        })
        .catch(this.logError)
        .finally(() => (this.pageLoading = false));
    },

    // ✅ Toggle status directly
    toggleCurrencyStatus(currency) {
      const updated = {
        id: currency.id,
        name: currency.name,
        activated: currency.activated ? 1 : 0,
      };

      this.pageLoading = true;
      this.$store
        .dispatch(PATCH, { url: "setting/currency", data: updated })
        .then(() => {
          const index = this.currencyList.findIndex(
            (c) => c.id === currency.id
          );
          if (index !== -1)
            this.$set(this.currencyList, index, { ...currency });
        })
        .catch((error) => {
          this.logError(error);
          // revert toggle if failed
          currency.activated = !currency.activated;
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },

    // Open Add/Edit Dialog
    openCurrencyDialog() {
      this.currencyPayload = { id: null, name: "", activated: true };
      this.currencyDialog = true;
    },

    editCurrency(currency) {
      this.currencyPayload = { ...currency };
      this.currencyDialog = true;
    },

    // Save currency (create/update)
    saveCurrency() {
      const errors = this.validateForm(this.$refs.currencyForm);
      if (errors.length) {
        errors.forEach((e) =>
          ErrorEventBus.$emit("update:error", InitializeError(e))
        );
        return;
      }
      this.pageLoading = true;
      this.$store
        .dispatch(PATCH, {
          url: "setting/currency",
          data: this.currencyPayload,
        })
        .then(() => {
          this.currencyDialog = false;
          this.getCurrencies();
        })
        .catch(this.logError)
        .finally(() => (this.pageLoading = false));
    },

    // Delete confirmation
    openDeleteDialog(currency) {
      this.selectedCurrencyId = currency.id;
      this.deleteDialog = true;
    },

    confirmDelete() {
      this.deleteLoading = true;
      this.$store
        .dispatch(DELETE, {
          url: `setting/currency/${this.selectedCurrencyId}`,
        })
        .then(() => {
          this.deleteDialog = false;
          SuccessEventBus.$emit(
            "update:success",
            "Currency deleted successfully!"
          );
          this.getCurrencies();
        })
        .catch(this.logError)
        .finally(() => (this.deleteLoading = false));
    },

    handlePageChange(page) {
      this.currentPage = page;
    },
  },
  mounted() {
    this.getCurrencies();
  },
};
</script>

<style scoped>
.currency-list-card {
  position: relative;
  min-height: calc(85vh - 16px);
}

.currency-list-footer {
  position: absolute;
  right: 24px;
  bottom: 0;
  left: 24px;
  min-height: 56px;
  margin: 0 !important;
  background-color: #fff;
  z-index: 2;
}
</style>
